import { recipe } from "@vanilla-extract/recipes";
import { rem } from "@huub-web/utils/css/rem";
import { style, keyframes } from "@vanilla-extract/css";

import { activeState } from "../../theme/activeState";
import { transition } from "../../utils/transition";
import { disabledState } from "../../theme/disabledState";
import { responsiveContainer } from "../../utils/responsive";
import { alignVariants } from "../../utils/vanilla/alignVariants";

import { groupStyle } from "../Group/Group.css";
import { theme } from "../../theme/css/theme.css";

const scaleAnimation = keyframes({
  "0%, 100%": {
    transform: "translate(-50%, -50%) scale(1)",
  },
  "50%": {
    transform: "translate(-50%, -50%) scale(1.15)",
  },
});

export const buttonContainerStyle = recipe({
  base: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    maxWidth: "100%",
    transition: transition(
      "color",
      "outline",
      "background-color",
      "border-color",
      "opacity",
      {
        property: "transform",
        duration: theme.transition.duration.xs,
      },
    ),
    borderRadius: theme.borderRadiuses.s,
    userSelect: "none",
    selectors: {
      "&:not([disabled])": {
        cursor: "pointer",
      },
      "&:not([disabled]):active": {
        ...activeState,
      },
      "&[disabled]": {
        ...disabledState,
        pointerEvents: "none",
      },

      [`${groupStyle} &:not(:first-child):not(:last-child)`]: {
        borderRadius: "0",
      },

      [`${groupStyle} &:hover`]: {
        zIndex: 2,
      },

      [`${groupStyle}[data-direction="vertical"] &:first-child:not(:last-child)`]:
        {
          borderBottomRightRadius: "0",
          borderBottomLeftRadius: "0",
        },
      [`${groupStyle}[data-direction="vertical"] &:last-child:not(:first-child)`]:
        {
          borderTopLeftRadius: "0",
          borderTopRightRadius: "0",
        },
      [`${groupStyle}[data-direction="vertical"] &:not(:first-child)`]: {
        marginTop: "-1px",
      },

      [`${groupStyle}[data-direction="horizontal"] &:first-child:not(:last-child)`]:
        {
          borderTopRightRadius: "0",
          borderBottomRightRadius: "0",
        },
      [`${groupStyle}[data-direction="horizontal"] &:last-child:not(:first-child)`]:
        {
          borderTopLeftRadius: "0",
          borderBottomLeftRadius: "0",
        },
      [`${groupStyle}[data-direction="horizontal"] &:not(:first-child)`]: {
        marginLeft: "-1px",
      },
    },
  },
  variants: {
    width: {
      auto: {},
      stretch: {
        width: "100%",
      },
    },
    grow: {
      true: {
        flexGrow: 1,
      },
      false: {},
    },
    alignSelf: {
      ...alignVariants("alignSelf"),
    },
    justifySelf: {
      ...alignVariants("justifySelf"),
    },
    size: {
      xxs: {
        height: rem(28),
        borderRadius: theme.borderRadiuses.xs2,
        padding: `0 ${theme.spacings.s}`,
        gap: rem(4),
      },
      xs: {
        height: rem(40),
        padding: `0 ${theme.spacings.s}`,
        gap: rem(6),
        borderRadius: theme.borderRadiuses.xs2,
      },
      s: {
        height: rem(48),
        padding: `0 ${theme.spacings.s}`,
        gap: rem(10),
      },
      m: {
        height: rem(56),
        padding: `0 ${theme.spacings.m}`,
        gap: rem(12),
      },
      l: {
        height: rem(72),
        padding: `0 ${theme.spacings.m}`,
        gap: rem(14),
      },
      input: {
        padding: `calc(${theme.spacings.s} - ${rem(4)}) ${theme.spacings.m}`,
      },
      inline: {
        height: rem(38),
        padding: `0 ${theme.spacings.s}`,
        gap: rem(6),
        borderRadius: theme.borderRadiuses.xs,
        ...responsiveContainer({
          500: {
            height: rem(48),
            padding: `0 ${theme.spacings.s}`,
            gap: rem(10),
            borderRadius: theme.borderRadiuses.s,
          },
        }),
      },
    },
  },
  defaultVariants: {
    size: "s",
  },
});

export const buttonTextStyle = recipe({
  base: {},
  variants: {
    size: {
      xxs: {
        fontSize: rem(14),
      },
      xs: {
        fontSize: rem(15),
      },
      s: {},
      m: {},
      l: {},
      input: {},
      inline: {},
    },
  },
});

export const buttonVariantStyle = recipe({
  variants: {
    variant: {
      primary: {
        backgroundColor: theme.colors.primary,
        color: theme.colors.onPrimary,
        vars: {
          "--background": theme.colors.primary,
        },
        outlineOffset: "2px",
        selectors: {
          "&:not(.--touch):not([disabled]):hover, &:not(.--touch):not([disabled])[data-active]":
            {
              borderColor: theme.colors.primaryHover,
              backgroundColor: theme.colors.primaryHover,
              color: theme.colors.onPrimaryHover,
              vars: {
                "--background": theme.colors.primaryHover,
              },
            },
        },
      },
      secondary: {
        backgroundColor: theme.colors.secondary,
        color: theme.colors.onSecondary,
        vars: {
          "--background": theme.colors.secondary,
        },
        selectors: {
          "&:not(.--touch):not([disabled]):hover, &:not(.--touch):not([disabled])[data-active]":
            {
              backgroundColor: theme.colors.secondaryHover,
              color: theme.colors.onSecondaryHover,
              vars: {
                "--background": theme.colors.secondaryHover,
              },
            },
        },
      },
      secondaryError: {
        backgroundColor: theme.colors.secondary,
        color: theme.colors.onSecondary,
        vars: {
          "--background": theme.colors.secondary,
        },
        selectors: {
          "&:not(.--touch):not([disabled]):hover, &:not(.--touch):not([disabled])[data-active]":
            {
              backgroundColor: theme.colors.error,
              color: theme.colors.onError,
            },
        },
      },
      tertiary: {
        backgroundColor: theme.colors.tertiary,
        color: theme.colors.onTertiary,
        vars: {
          "--background": theme.colors.tertiary,
        },
        selectors: {
          "&:not(.--touch):not([disabled]):hover, &:not(.--touch):not([disabled])[data-active]":
            {
              borderColor: theme.colors.tertiaryHover,
              backgroundColor: theme.colors.tertiaryHover,
              color: theme.colors.onTertiaryHover,
              vars: {
                "--background": theme.colors.tertiaryHover,
              },
            },
        },
      },
      opaque: {
        backgroundColor: "transparent",
        vars: {
          "--background": theme.colors.primary,
        },
        selectors: {
          "&:not(.--touch):not([disabled]):hover, &:not(.--touch):not([disabled])[data-active]":
            {
              borderColor: theme.colors.primary,
              backgroundColor: theme.colors.primary,
              color: theme.colors.onPrimary,
              vars: {
                "--background": theme.colors.primary,
              },
            },
        },
      },
      opaqueError: {
        backgroundColor: "transparent",
        color: theme.colors.onBackground,
        vars: {
          "--background": theme.colors.error,
          "--border-color__hover": theme.colors.errorHover,
        },
        selectors: {
          "&:not(.--touch):not([disabled]):hover, &:not(.--touch):not([disabled])[data-active]":
            {
              backgroundColor: theme.colors.error,
              color: theme.colors.onErrorHover,
            },
        },
      },
      opaqueCurrentColor: {
        backgroundColor: "transparent",
        color: "currentColor",
        selectors: {
          "&:before": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "100%",
            height: "100%",
            transform: "translate(-50%, -50%)",
            zIndex: 2,
            borderRadius: "inherit",
            transition: transition("opacity", "background-color"),
            opacity: 0.1,
            backgroundColor: "transparent",
          },
          "&:not(.--touch):not([disabled]):hover:before, &:not(.--touch):not([disabled])[data-active]:before":
            {
              backgroundColor: "currentColor",
            },
        },
      },
      background: {
        backgroundColor: theme.colors.background,
        color: theme.colors.onBackground,
        vars: {
          "--background": theme.colors.background,
        },
        selectors: {
          "&:not(.--touch):not([disabled]):hover, &:not(.--touch):not([disabled])[data-active]":
            {
              backgroundColor: theme.colors.primary,
              color: theme.colors.onPrimary,
              borderColor: theme.colors.primary,
              vars: {
                "--background": theme.colors.primary,
              },
            },
        },
      },
      backgroundSecondary: {
        backgroundColor: theme.colors.background,
        color: theme.colors.onBackground,
        vars: {
          "--background": theme.colors.background,
        },
        selectors: {
          "&:not(.--touch):not([disabled]):hover, &:not(.--touch):not([disabled])[data-active]":
            {
              backgroundColor: theme.colors.secondary,
              color: theme.colors.onSecondary,
              borderColor: theme.colors.secondary,
              vars: {
                "--background": theme.colors.secondary,
              },
            },
        },
      },
      info: {
        backgroundColor: theme.colors.info,
        color: theme.colors.onInfo,
        outlineOffset: "2px",
        vars: {
          "--background": theme.colors.info,
        },
        selectors: {
          "&:not(.--touch):not([disabled]):hover, &:not(.--touch):not([disabled])[data-active]":
            {
              borderColor: theme.colors.infoHover,
              backgroundColor: theme.colors.infoHover,
              color: theme.colors.onInfoHover,
              vars: {
                "--background": theme.colors.infoHover,
              },
            },
        },
      },
      error: {
        backgroundColor: theme.colors.error,
        color: theme.colors.onError,
        vars: {
          "--background": theme.colors.error,
        },
        selectors: {
          "&:not(.--touch):not([disabled]):hover, &:not(.--touch):not([disabled])[data-active]":
            {
              borderColor: theme.colors.errorHover,
              backgroundColor: theme.colors.errorHover,
              color: theme.colors.onErrorHover,
              vars: {
                "--background": theme.colors.errorHover,
              },
            },
        },
      },
      warning: {
        backgroundColor: theme.colors.warning,
        color: theme.colors.onWarning,
        vars: {
          "--background": theme.colors.warning,
        },
        selectors: {
          "&:not(.--touch):not([disabled]):hover, &:not(.--touch):not([disabled])[data-active]":
            {
              borderColor: theme.colors.warningHover,
              backgroundColor: theme.colors.warningHover,
              color: theme.colors.onWarningHover,
              vars: {
                "--background": theme.colors.warningHover,
              },
            },
        },
      },
      success: {
        backgroundColor: theme.colors.success,
        color: theme.colors.onSuccess,
        vars: {
          "--background": theme.colors.success,
        },
        selectors: {
          "&:not(.--touch):not([disabled]):hover, &:not(.--touch):not([disabled])[data-active]":
            {
              borderColor: theme.colors.successHover,
              backgroundColor: theme.colors.successHover,
              color: theme.colors.onSuccessHover,
              vars: {
                "--background": theme.colors.successHover,
              },
            },
        },
      },
      gradient: {
        position: "relative",
        background: theme.colors.background,
        color: theme.colors.onBackground,
        selectors: {
          "&:after": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            height: "100%",
            background: `linear-gradient(103.85deg, ${theme.colors.gradientA} -0.39%, ${theme.colors.gradientB} 23.95%, ${theme.colors.gradientC} 47.38%, ${theme.colors.gradientD} 72.64%)`,
            borderRadius: "inherit",
            filter: "blur(6px)",
            opacity: 0.6,
            zIndex: 1,
            transition: transition("opacity", "filter"),
            pointerEvents: "none",
            animation: `${scaleAnimation} 6s ease-in-out infinite`,
          },
          "&:before": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "100%",
            height: "100%",
            transform: "translate(-50%, -50%)",
            background: theme.colors.background,
            zIndex: 2,
            borderRadius: "inherit",
            transition: transition("opacity", "background-color"),
          },
          "&:not(.--touch):not([disabled]):hover:before, &:not(.--touch):not([disabled])[data-active]:before":
            {
              backgroundColor: theme.colors.panel,
            },
        },
      },
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

export const buttonEdgesStyle = style({
  zIndex: 2,
  position: "relative",
});
